import {
    useEffect,
    useState,
    type FC,
    useRef,
    useId
} from "react";
import {
    AccessibilityInfo,
    Platform,
    View
} from "react-native";
import type IFileUploadProps from "./type";
import type {
    FileUploadIncomingFile,
    FileUploadRemovedEntry,
    FileUploadSnapshot,
    FileUploadSource,
    IFileRejection,
    IUploadFile
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import FileUploadAvatarUpload from "./components/avatarUpload";
import FileUploadDropzone from "./components/dropzone";
import FileUploadGallery from "./components/gallery";
import FileUploadList from "./components/list";
import FileUploadSourceSheet from "./components/sourceSheet";
import FileUploadTrigger from "./components/trigger";
import {
    useFilePreviewRelease,
    useFilePicker
} from "./hook";
import {
    FILE_UPLOAD_DEFAULT_MAX_FILES,
    FILE_UPLOAD_EMPTY_FILES,
    getUploadAnnouncement,
    getDescriptionText,
    getHasImageAccept,
    createUploadFile,
    getIsImageAccept,
    validateFiles,
    getRows
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    CircleAlert as CircleAlertIcon
} from "lucide-react-native";
import {
    formatNumber
} from "../../helpers/locale";
import Alert from "../alert";
import Text from "../text";

const FileUpload: FC<IFileUploadProps> = ({
    maxFiles = FILE_UPLOAD_DEFAULT_MAX_FILES,
    files = FILE_UPLOAD_EMPTY_FILES,
    variant = "dropzone",
    isDisabled = false,
    isMultiple = false,
    size = "medium",
    customLocalize,
    onSourcePress,
    customTheme,
    description,
    onChange,
    onReject,
    onRemove,
    maxSize,
    onRetry,
    sources,
    accept,
    style,
    title,
    ...props
}) => {
    const {
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const baseId = useId();

    const [
        removedEntries,
        setRemovedEntries
    ] = useState<Array<FileUploadRemovedEntry>>([]);

    const [
        rejections,
        setRejections
    ] = useState<Array<IFileRejection>>([]);

    const [
        announcement,
        setAnnouncement
    ] = useState<string>("");

    const [
        isSheetOpen,
        setIsSheetOpen
    ] = useState<boolean>(false);

    const isMountedRef = useRef(false);

    const previousRef = useRef<Map<string, FileUploadSnapshot>>(new Map(files.map((file) => [
        file.key,
        {
            progress: file.progress,
            status: file.status
        }
    ])));

    const {
        container: containerDynamicStyle,
        helper: helperDynamicStyle,
        status: statusDynamicStyle
    } = useStyles({
        borders,
        spaces
    });

    const labelId = `${baseId}-label`;

    const titleId = `${baseId}-title`;

    const descriptionId = `${baseId}-description`;

    const helperId = `${baseId}-helper`;

    const isTouch = pointerType === "coarse";

    const isGallery = variant === "dropzone" && isMultiple && getIsImageAccept({
        accept
    });

    const isShowGallery = isGallery && files.length > 0;

    const currentSources = (sources ?? (variant === "avatar" ? [
        "camera",
        "library"
    ] : [
        "camera",
        "library",
        "files"
    ])).filter((source) => source === "files" || getHasImageAccept({
        accept
    }));

    const descriptionText = description ?? getDescriptionText({
        locale: activeLocale,
        isMultiple,
        localize,
        maxFiles,
        maxSize,
        accept
    });

    useEffect(() => {
        isMountedRef.current = true;
    }, []);

    useEffect(() => {
        const message = getUploadAnnouncement({
            previous: previousRef.current,
            locale: activeLocale,
            localize,
            files
        });

        previousRef.current = new Map(files.map((file) => [
            file.key,
            {
                progress: file.progress,
                status: file.status
            }
        ]));

        if(!message) {
            return;
        }

        setAnnouncement(message);

        if(Platform.OS === "ios") {
            AccessibilityInfo.announceForAccessibility(message);
        }
    }, [
        activeLocale,
        files
    ]);

    const handleFiles = (incoming: Array<FileUploadIncomingFile>) => {
        const {
            rejections: nextRejections,
            accepted
        } = validateFiles({
            currentCount: isMultiple ? files.length : 0,
            locale: activeLocale,
            isMultiple,
            incoming,
            localize,
            maxFiles,
            maxSize,
            accept
        });

        setRemovedEntries([]);

        setRejections(nextRejections);

        if(nextRejections.length) {
            onReject?.(nextRejections);
        }

        if(!accepted.length) {
            return;
        }

        const added = accepted.map((item, index) => createUploadFile({
            index,
            item
        }));

        onChange?.(isMultiple ? [
            ...files,
            ...added
        ] : added);
    };

    const {
        renderInput,
        openPicker
    } = useFilePicker({
        onFiles: handleFiles,
        isDisabled,
        isMultiple,
        accept
    });

    useFilePreviewRelease({
        removedEntries,
        files
    });

    const handleSourcePress = (source: FileUploadSource) => {
        onSourcePress?.(source);

        openPicker({
            source
        });
    };

    const handleOpen = () => {
        if(isDisabled) {
            return;
        }

        if(!isTouch) {
            openPicker();

            return;
        }

        const [
            firstSource
        ] = currentSources;

        if(currentSources.length === 1 && firstSource) {
            handleSourcePress(firstSource);

            return;
        }

        setIsSheetOpen(true);
    };

    const handleRemove = (file: IUploadFile) => {
        const index = files.findIndex((item) => item.key === file.key);

        const status = file.status ?? "queued";

        const isUndoable = variant !== "avatar" && !isShowGallery && (status === "done" || status === "error");

        if(isUndoable) {
            setRemovedEntries((previous) => [
                ...previous,
                {
                    index,
                    file
                }
            ]);
        }

        onRemove?.(file.key);

        onChange?.(files.filter((item) => item.key !== file.key));
    };

    const handleUndo = (entry: FileUploadRemovedEntry) => {
        const next = [
            ...files
        ];

        next.splice(Math.min(entry.index, next.length), 0, entry.file);

        setRemovedEntries((previous) => previous.filter((item) => item.file.key !== entry.file.key));

        onChange?.(next);
    };

    const handleRetry = (file: IUploadFile) => {
        onRetry?.(file.key);
    };

    const handleCloseAlert = () => {
        setRejections([]);
    };

    const handleSheetClosed = () => {
        setIsSheetOpen(false);
    };

    const getHelper = () => {
        const [
            firstRejection
        ] = rejections;

        if(rejections.length === 1 && firstRejection) {
            return {
                text: firstRejection.message,
                isDanger: true
            };
        }

        if(variant === "button") {
            return {
                text: descriptionText,
                isDanger: false
            };
        }

        if(variant === "dropzone" && isMultiple && files.length) {
            return {
                text: localize("file-upload-count", [
                    formatNumber({
                        locale: activeLocale,
                        value: files.length
                    }),
                    formatNumber({
                        locale: activeLocale,
                        value: maxFiles
                    })
                ]),
                isDanger: false
            };
        }

        return {
            isDanger: false,
            text: ""
        };
    };

    const helper = getHelper();

    const renderLabel = () => {
        if(!title || variant === "avatar") {
            return null;
        }

        return <Text
            customColor={isDisabled ? colors.system.state.content.disabled.neutral : undefined}
            color={isDisabled ? undefined : "high"}
            customTheme={customTheme}
            variant="labelMediumSize"
            id={labelId}
        >
            {title}
        </Text>;
    };

    const renderControl = () => {
        if(variant === "avatar") {
            return <FileUploadAvatarUpload
                onRemove={files[0] ? () => {
                    handleRemove(files[0] as IUploadFile);
                } : undefined}
                customLocalize={customLocalize}
                description={descriptionText}
                descriptionId={descriptionId}
                customTheme={customTheme}
                isDisabled={isDisabled}
                onPress={handleOpen}
                isTouch={isTouch}
                file={files[0]}
                title={title}
                size={size}
            />;
        }

        if(variant === "button") {
            return <FileUploadTrigger
                accessibilityLabel={title ? `${title}, ${localize("file-upload-select-file")}` : undefined}
                onRemove={files[0] ? () => {
                    handleRemove(files[0] as IUploadFile);
                } : undefined}
                customLocalize={customLocalize}
                customTheme={customTheme}
                fileName={files[0]?.name}
                isDisabled={isDisabled}
                onPress={handleOpen}
                size={size}
            />;
        }

        if(isShowGallery) {
            return null;
        }

        return <FileUploadDropzone
            {...{
                "aria-describedby": helper.text ? `${descriptionId} ${helperId}` : descriptionId
            }}
            aria-labelledby={title ? `${labelId} ${titleId}` : titleId}
            isInvalid={rejections.length > 0}
            customLocalize={customLocalize}
            description={descriptionText}
            descriptionId={descriptionId}
            customTheme={customTheme}
            isDisabled={isDisabled}
            isMultiple={isMultiple}
            onFiles={handleFiles}
            onPress={handleOpen}
            isTouch={isTouch}
            titleId={titleId}
            size={size}
        />;
    };

    const renderHelper = () => {
        if(!helper.text) {
            return null;
        }

        return <View
            role={helper.isDanger ? "alert" : undefined}
            style={[
                stylesheet.helper,
                helperDynamicStyle
            ]}
            id={helperId}
        >
            {
                helper.isDanger ?
                    <CircleAlertIcon
                        color={colors.content.icon.danger}
                        size={iconSizes.small}
                    />
                :
                    null
            }
            <Text
                color={helper.isDanger ? "danger" : "mid"}
                style={stylesheet.helperText}
                customTheme={customTheme}
                variant="bodySmallSize"
            >
                {helper.text}
            </Text>
        </View>;
    };

    const renderAlert = () => {
        if(rejections.length < 2) {
            return null;
        }

        return <Alert
            description={rejections.map((rejection) => `• ${rejection.message}`).join("\n")}
            title={localize("file-upload-rejected-title", [
                formatNumber({
                    value: rejections.length,
                    locale: activeLocale
                })
            ])}
            customLocalize={customLocalize}
            onClose={handleCloseAlert}
            customTheme={customTheme}
            isClosable={true}
            variant="tonal"
            type="danger"
        />;
    };

    const renderItems = () => {
        if(variant === "avatar" || !files.length && !removedEntries.length) {
            return null;
        }

        if(isShowGallery) {
            return <FileUploadGallery
                isInitialAnimated={isMountedRef.current}
                isShowAdd={files.length < maxFiles}
                customLocalize={customLocalize}
                customTheme={customTheme}
                onRemove={handleRemove}
                isDisabled={isDisabled}
                onRetry={handleRetry}
                onAdd={handleOpen}
                title={title}
                files={files}
                size={size}
            />;
        }

        const isButtonDone = variant === "button" && files.every((file) => file.status === "done");

        if(isButtonDone && !removedEntries.length) {
            return null;
        }

        return <FileUploadList
            isInitialAnimated={isMountedRef.current}
            rows={getRows({
                files: isButtonDone ? [] : files,
                removedEntries
            })}
            customLocalize={customLocalize}
            customTheme={customTheme}
            isDisabled={isDisabled}
            onRemove={handleRemove}
            onRetry={handleRetry}
            onUndo={handleUndo}
            title={title}
            size={size}
        />;
    };

    const renderSheet = () => {
        if(!isTouch || currentSources.length < 2) {
            return null;
        }

        return <FileUploadSourceSheet
            onRemovePhoto={variant === "avatar" && files[0] ? () => {
                handleRemove(files[0] as IUploadFile);
            } : undefined}
            variant={variant === "avatar" ? "avatar" : "files"}
            onSourcePress={handleSourcePress}
            customLocalize={customLocalize}
            description={descriptionText}
            onClosed={handleSheetClosed}
            customTheme={customTheme}
            sources={currentSources}
            isOpen={isSheetOpen}
        />;
    };

    return <View
        {...props}
        aria-labelledby={title && variant !== "avatar" ? labelId : undefined}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="group"
    >
        {renderLabel()}
        {renderControl()}
        {renderHelper()}
        {renderAlert()}
        {renderItems()}
        <View
            style={[
                stylesheet.status,
                statusDynamicStyle
            ]}
            aria-live="polite"
            role="status"
        >
            {
                announcement ?
                    <Text
                        customTheme={customTheme}
                    >
                        {announcement}
                    </Text>
                :
                    null
            }
        </View>
        {renderInput()}
        {renderSheet()}
    </View>;
};
export default FileUpload;
